import * as Actions from 'actions'
import cx from 'classnames'
import { Box } from '@material-ui/core'
import { TranslationContext } from 'contexts/TranslationContext'
import { useAction, useModalDialog, useOnClickWithoutPropagation } from 'hooks'
import { isEqual, noop, some } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import { SelectableGroup } from 'react-selectable-fast-control'
import { useHotkeys } from 'react-hotkeys-hook'
import * as PT from '~/PropTypes'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import Scrollbars from '~/components/base/Scrollbars'
import { DRAGNDROP_TYPE, MENU_ITEMS, SOURCE_FILE_TYPES, HOTKEYS, FILE_TYPE } from '~/enums'
import { useRefWithSetter } from '~/hooks/utils'
import * as Assets from '~/models/Asset'
import { Folder } from '~/models/Folder'
import * as Selectors from '~/selectors'
import styles from './BaseAssetsList.module.scss'
import './BaseAssetsList.scss'
import { Orientation } from '~/types/userInterface'
import { TransformUIDropContainer } from '~/components/TransformUIDropContainer'
import { DROP_CONTAINER, DISPLAY_TYPE, UI_ELEMENT } from '~/config/constants/ui'
import { useTransformUI } from '~/hooks/useTransformUI'
import { selectElementDisplayType, selectSplitSize, selectActiveInteractionUIElement } from '~/selectors/useInterface'
import { SOURCE_MEDIA_ITEM_HEIGHT, SOURCE_MEDIA_ITEM_WIDTH, MEDIA_BLOCK_WIDTH_FOR_FIX_AUDIO_ITEM } from '~/constant'
import { getExtension } from '~/components/Projects/components/Import/utils'
import { setActiveDragElement, setReferenceFileChoice, showDeleMediaFilesFormAction } from '~/actions/mainView'
import { isVideo } from '~/Util/assets'
import { getIncompatibleFileExtensions } from '~/incompatibleFileExtensionsMap'
import { selectShowReferenceFileChoice } from '~/selectors/mainView'
import { activeHotkeyProfileSelector } from '~/selectors/user-profile-selector'
import { MediaUsedOnDeletionError } from '~/errors/MediaUsedOnDeletionError'
import { ConfirmationDialog } from '~/components/base/ConfirmationDialog/ConfirmationDialog'
import { setActiveInteractionUIElement } from '~/actions/userInterface'

const empty = () => {}

export const BaseAssetsListContext = React.createContext({
  /** @type {UploadableAsset[]} */
  selectedItems: [],
  setSelectedItems: () => {},
  folders: [],
  deletionDialog: { open: noop, cancel: noop, confirm: noop, active: false },
  hotkeyDeletionDialog: { open: noop, cancel: noop, confirm: noop, active: false },
  foldersDialog: { open: noop, cancel: noop, confirm: noop, active: false },
  handleFolderSelect: noop,
  onMoveMediaItemsToFolder: noop,
  selectedFolder: null,
  onOpenContextMenu: noop,
  onCloseContextMenu: noop,
  isOpenContextMenu: false,
  menuPositionCoords: {},
  clickedMediaElemId: null,
  setSelectedFolder: noop,
  isOpenDialog: false,
})

function BaseAssetsList(props) {
  const {
    className,
    /** @type {Asset[]} */
    items,
    loading,
    header,
    ItemComponent,
    before,
    foldersNavBar,
    placeholder,
    after,
    float,
    onScroll,
    fullHeightScrollbar,
    classes,
  } = props
  const {
    selectedItems,
    setSelectedItems,
    hotkeyDeletionDialog,
  } = useContext(BaseAssetsListContext)
  const refFiles = useSelector(selectShowReferenceFileChoice)
  const [ selectableGroupRef, setSelectableGroupRef ] = useRefWithSetter()
  const onMoveMediaItemsToFolder = useAction(Actions.sourceFiles.moveMediaItemsToFolder)
  const onSetReferenceFileChoice = useAction(setReferenceFileChoice)
  const onSetActiveInteractionUIElement = useAction(setActiveInteractionUIElement)
  const { menuItem } = useSelector(state => state.mainView)
  const { media: mediaColumnSize } = useSelector(selectSplitSize)
  const assetsList = useRef(null)
  const handleSelectionClear = noClear => {
    setSelectedItems(prev => {
      if (noClear) {
        return prev
      }
      return []
    })
  }
  const [ , drag ] = useDraggable(
    {
      onMoveMediaItemsToFolder,
      handleSelectionClear,
    },
    selectedItems
  )

  const isItemsDraggable = selectedItems.length <= 1

  const handleItemMouseDown = __CFG__.SOURCE_FILES_MANAGEMENT.SELECTABLE
    ? (assetId, fn) => {
      if (!selectedItems.map(item => item.id).includes(assetId)) {
        selectableGroupRef.current.clearSelection()
        fn()
      }
    }
    : empty

  const handleItemClick = __CFG__.SOURCE_FILES_MANAGEMENT.SELECTABLE
    ? (assetId, fn) => {
      const item = selectedItems.find(item => item.id === assetId)
      if (item) {
        selectableGroupRef.current.clearSelection()
        fn()
        // called after handleSelectionClear
        setTimeout(() => {
          onSetReferenceFileChoice([ item ])
        })
      }
    }
    : empty

  const onSelectionFinish = selectedElems => {
    if (selectedElems.length === 0) return
    const items = selectedElems.map(item => ({
      id: item.props.asset.id,
      name: item.props.asset.name,
      type: getDraggableType(item.props.asset),
      hasError: item.props.asset.hasError,
      uploading: item.props.asset.uploading,
      originalUrl: item.props.asset?.originalUrl || '',
      path: item.props.asset?.path || '',
      fileExtension: getExtension(item.props.asset.name),
      filetype: item.props.asset.filetype,
    }))
    setSelectedItems(() => items)
    onSetReferenceFileChoice(items)
  }

  useEffect(() => {
    if (!isEqual(selectedItems, refFiles)) {
      onSetReferenceFileChoice(selectedItems)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedItems, refFiles ])

  useEffect(() => {
    const assetsListContainer = assetsList.current
    const setInteractionUIElement = () => {
      onSetActiveInteractionUIElement(UI_ELEMENT.SOURCE_LIST)
    }
    if (assetsListContainer) {
      assetsListContainer.addEventListener('mouseup', setInteractionUIElement)
    }
    return () => {
      if (assetsListContainer) {
        assetsListContainer.removeEventListener('mouseup', setInteractionUIElement)
      }
    }
  }, [ assetsList, onSetActiveInteractionUIElement ])

  const Wrapper = useMemo(
    () => (fullHeightScrollbar ? 'div' : Scrollbars),
    [ fullHeightScrollbar ]
  )
  const { t } = useContext(TranslationContext)
  const { drag: setDrag, isDragging } = useTransformUI({ type: DRAGNDROP_TYPE.SOURCE_LIST_HEADER })

  const sourceFileRef = useRef()
  const { clientHeight: assetHeight, clientWidth: assetWidth } = sourceFileRef.current ?? {}
  const countRowSourceAssets = Math.floor(mediaColumnSize / (assetWidth ?? SOURCE_MEDIA_ITEM_WIDTH))
  const countSourceAssets = menuItem === MENU_ITEMS.MEDIA
    ? (items?.length ?? 0) + 1
    : (items?.length ?? 0)
  const groupHeight = (Math.floor(countSourceAssets / countRowSourceAssets)
    * (assetHeight ?? SOURCE_MEDIA_ITEM_HEIGHT))
  return (
    <div className={cx('assets-list', className)} ref={assetsList}>
      {isDragging && (
        <TransformUIDropContainer
          orientation={Orientation.Horizontal}
          width="100%"
          height="10px"
          dropContainer={DROP_CONTAINER.HEADER_MENU}
        />
      )}
      <SourceMediaListHeader isDragging={isDragging} setDrag={setDrag} header={header} />
      <Choose>
        <When condition={loading}>
          <CircularProgress
            size={100}
            endless
            percents={80}
            text={`${t('MEDIA_PROGRESS_TEXT_LOADING')}..`}
            transparent
          />
        </When>
        <Otherwise>
          <div>{foldersNavBar}</div>
          <Wrapper
            {...(!fullHeightScrollbar
              ? {
                renderViewClassName: 'media-scrollbar',
                onScrollFrame: __CFG__.SOURCE_FILES_MANAGEMENT
                  .LOAD_MEDIA_ONSCROLL
                  ? onScroll
                  : empty,
              }
              : { style: { height: '100%' } })}
          >
            <Choose>
              <When condition={!items?.length}>{placeholder}</When>
              <Otherwise>
                <SelectableGroup
                  style={{
                    height: selectableGroupRef.current?.scrollContainer.clientHeight < groupHeight
                      ? 'fit-content'
                      : groupHeight,
                    minHeight: '100%',
                  }}
                  ref={setSelectableGroupRef}
                  className={cx(`${styles.selectableGroup}
                    assets-list__selectable-group`)}
                  disabled={
                    !__CFG__.SOURCE_FILES_MANAGEMENT.SELECTABLE
                    || menuItem !== MENU_ITEMS.MEDIA
                  }
                  selectboxClassName={styles.selectBox}
                  scrollContainer=".scroll-container__content.media-scrollbar"
                  selectOnClick
                  deselectOnEsc={false}
                  resetOnStart
                  allowCtrlClick
                  allowShiftClick
                  ignoreList={[ '.source-asset div', '.source-asset.not-selectable' ]}
                  onSelectionClear={handleSelectionClear}
                  onSelectionFinish={onSelectionFinish}
                >
                  <div
                    className={cx('assets-list__items', classes.list)}
                    ref={drag}
                  >
                    {before}
                    {items.map((item, index) => (
                      <Box
                        ref={sourceFileRef}
                        style={{
                          width: menuItem === MENU_ITEMS.AUDIO
                          && mediaColumnSize < MEDIA_BLOCK_WIDTH_FOR_FIX_AUDIO_ITEM
                            ? '100%'
                            : 'fit-content',
                        }}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item.id}-${index}`}
                      >
                        {ItemComponent({
                          asset: item,
                          draggable: isItemsDraggable,
                          onItemMouseDown: handleItemMouseDown,
                          onItemClick: handleItemClick,
                        })}
                      </Box>
                    ))}
                  </div>
                </SelectableGroup>
                <If condition={hotkeyDeletionDialog.active}>
                  <ConfirmationDialog
                    onClick={e => e.stopPropagation()}
                    onCancel={hotkeyDeletionDialog.cancel}
                    onConfirm={hotkeyDeletionDialog.confirm}
                    confirmBtnText={t('MEDIA_CONFIRM_BTN_DELETE')}
                    closeBtnText={t('MEDIA_CANCEL_BTN_DELETE')}
                    description={t('MEDIA_CONFIRM_DESC_ARE_YOU_SURE_TO_REMOVE_SELECTED')}
                  />
                </If>
              </Otherwise>
            </Choose>
            {after}
          </Wrapper>
          <div>{float}</div>
        </Otherwise>
      </Choose>
    </div>
  )
}

BaseAssetsList.propTypes = {
  items: PropTypes.arrayOf(PT.MediaItem),
  loading: PropTypes.bool,
  header: PropTypes.element.isRequired,
  ItemComponent: PropTypes.elementType.isRequired,
  before: PropTypes.element,
  foldersNavBar: PropTypes.element,
  placeholder: PropTypes.element,
  after: PropTypes.element,
  float: PropTypes.element,
  onScroll: PropTypes.func,
  fullHeightScrollbar: PropTypes.bool,
  classes: PropTypes.shape({ root: PropTypes.string, list: PropTypes.string }),
}

BaseAssetsList.defaultProps = {
  items: [],
  loading: false,
  before: null,
  foldersNavBar: null,
  placeholder: null,
  after: null,
  float: null,
  onScroll: () => {},
  fullHeightScrollbar: false,
  classes: { root: '', list: '' },
}

function getDraggableType(asset) {
  switch (asset.constructor) {
    case Assets.VideoAsset:
      return DRAGNDROP_TYPE.MEDIA_ITEM
    case Assets.ImageAsset:
      return DRAGNDROP_TYPE.IMAGE_ITEM
    case Assets.TextAsset:
      return DRAGNDROP_TYPE.TEXT_ITEM
    case Assets.AudioAsset:
      return DRAGNDROP_TYPE.AUDIO_ITEM
    case Assets.TransitionAsset:
      return DRAGNDROP_TYPE.TRANSITION_ITEM
    case Folder:
      return DRAGNDROP_TYPE.FOLDER
    default:
      throw new Error(`Unsupported asset type: ${asset.constructor.name}`)
  }
}

function useDraggable(options, ...deps) {
  const setDndDropTarget = useAction(Actions.mainView.setDndDropTarget)
  const onSetActiveDragElement = useAction(setActiveDragElement)
  const { handleSelectionClear, onDenied } = options
  const [ selectedItems ] = deps

  const item = {
    type: DRAGNDROP_TYPE.GROUP,
    selectedItems,
    handleSelectionClear,
    incompatibleFileExtensions: selectedItems.reduce((res, item) => {
      const result = { ...res }
      if (isVideo(item)) {
        result[item.filetype] = getIncompatibleFileExtensions(item.filetype)
      }
      return result
    }, {}),
  }
  const [ collected, drag, preview ] = useDrag(
    {
      item,
      begin: () => {
        setDndDropTarget('root')
        onSetActiveDragElement(DRAGNDROP_TYPE.GROUP)
      },
      end() {
        onSetActiveDragElement(null)
      },
      canDrag() {
        if (
          some(selectedItems, item => item.uploading)
          && !some(selectedItems, item => item.hasError)
        ) {
          onDenied()
          return false
        }
        return selectedItems.length > 1
      },
    },
    deps
  )

  React.useEffect(() => {
    preview(getEmptyImage())
  }, [ preview ])
  return [ collected, drag ]
}

export function SourceMediaListHeader(
  { setDrag = noop, header, isDragging = false, asDragContainer }
) {
  const { sourceList: sourceListDisplayType } = useSelector(selectElementDisplayType)
  return (
    <div
      className={cx('assets-list__header', { row: sourceListDisplayType !== DISPLAY_TYPE.MIN_WIDTH })}
      ref={ref => {
        setDrag(ref)
      }}
      style={{
        opacity: isDragging && !asDragContainer ? 0 : 1,
      }}
    >
      {header}
    </div>
  )
}

// ---

function AssetsListContainer(props) {
  const { type, ...rest } = props
  const [ errorTitle, setErrorTitle ] = useState('')
  const [ errorMessage, setErrorMessage ] = useState('')
  const items = useSelector(state => Selectors.getSourceFiles(state, type))
  const loading = useSelector(state => Selectors.getIsSourceFilesLoading(state, type))
  const sourceFilesParams = useSelector(state => Selectors.selectSourceFilesParams(state, type))
  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const activeInteractionUIElement = useSelector(selectActiveInteractionUIElement)
  const selectedAssets = useSelector(Selectors.getSelectedAssets)
  const [ selectedItems, setSelectedItems ] = useState([])
  const [ selectedFolder, setSelectedFolder ] = useState(null)
  const [ isOpenContextMenu, setOpenContextMenu ] = useState(false)
  const [ clickedMediaElemId, setClickedMediaElemId ] = useState(null)
  const menuPositionCoords = useRef(null)

  const selectedItemsByType = useMemo(() => {
    setOpenContextMenu(false)
    return selectedItems.reduce((result, item) => {
      if (item.type === DRAGNDROP_TYPE.FOLDER) {
        result.folders.push(item.id)
      } else if (item.type !== DRAGNDROP_TYPE.FOLDER) {
        result.files.push(item.id)
      }
      return result
    }, { folders: [], files: [] })
  }, [ selectedItems ])

  const onDeleteMediaFiles = useAction(showDeleMediaFilesFormAction, { visibility: true })
  const onMoveMediaAction = useAction(Actions.sourceFiles.moveMediaItemsToFolder)
  const onMoveMediaItemsToFolder = (isFolder, id) => {
    onMoveMediaAction({
      folders: isFolder ? [ id ] : selectedItemsByType.folders,
      files: isFolder ? [] : selectedItemsByType.files,
      targetFolder: selectedFolder,
      type: SOURCE_FILE_TYPES.MEDIA,
    })
  }
  const deleteFolder = useAction(Actions.sourceFiles.requestDeleteFolder)
  const deleteFolderForce = useCallback(folderId => {
    deleteFolder(folderId, FILE_TYPE.FOLDER, true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const deletionDialog = useModalDialog({ onConfirm: onDeleteMediaFiles, onCancel })
  const hotkeyDeletionDialog = useModalDialog({ onConfirm: deleteWithHotkey, onCancel })
  const foldersDialog = useModalDialog({ onConfirm: onMoveMediaItemsToFolder, onCancel })
  const singleDeletion = useModalDialog({ onConfirm: deleteSingleMediaItem, onCancel })
  const forceDeletionFolderDialog = useModalDialog({ onConfirm: deleteFolderForce, onCancel })

  const isOpenDialog = deletionDialog.active
  || hotkeyDeletionDialog.active
  || foldersDialog.active
  || singleDeletion.active
  || forceDeletionFolderDialog.active

  const loadingShow = loading && sourceFilesParams.start === 0

  const onOpenContextMenu = useCallback((e, id) => {
    e.stopPropagation()
    menuPositionCoords.current = { x: e.clientX, y: e.clientY }
    setClickedMediaElemId(id)
    setOpenContextMenu(true)
  }, [])

  const onCloseContextMenu = useOnClickWithoutPropagation(() => {
    setOpenContextMenu(false)
    menuPositionCoords.current = null
  })

  const handleFolderSelect = useCallback(id => {
    setSelectedFolder(id)
  }, [])

  function onCancel() {
    setOpenContextMenu(false)
    setClickedMediaElemId(null)
  }

  function deleteWithHotkey() {
    if (!selectedAssets?.length) {
      onDeleteMediaFiles()
    }
  }

  function onFolderDeletionError(e) {
    if (e instanceof MediaUsedOnDeletionError) {
      const { title, type } = e
      setErrorTitle(title)
      setErrorMessage(type)
      forceDeletionFolderDialog.open()
    }
  }

  async function deleteSingleMediaItem({ isFolder, id }) {
    if (isFolder) {
      try {
        await deleteFolder(id, SOURCE_FILE_TYPES.MEDIA)
      } catch (e) {
        onFolderDeletionError(e)
      }
    } else {
      onDeleteMediaFiles()
    }
  }

  const onHotkeyAction = useCallback(() => {
    if (!selectedAssets?.length
      && selectedItems.length
      && activeInteractionUIElement === UI_ELEMENT.SOURCE_LIST) {
      hotkeyDeletionDialog.open()
    }
  }, [ selectedAssets, hotkeyDeletionDialog, selectedItems, activeInteractionUIElement ])

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.DELETE], onHotkeyAction)
  useEffect(() => {
    if (!foldersDialog.active) setSelectedFolder(null)
  }, [ foldersDialog.active, setSelectedFolder ])

  const context = {
    selectedItems,
    setSelectedItems,
    selectedFolder,
    setSelectedFolder,
    folders: selectedItemsByType.folders,
    handleFolderSelect,
    deletionDialog,
    foldersDialog,
    onMoveMediaItemsToFolder,
    onOpenContextMenu,
    onCloseContextMenu,
    clickedMediaElemId,
    setClickedMediaElemId,
    menuPositionCoords: menuPositionCoords.current,
    errorTitle,
    errorMessage,
    singleDeletion,
    forceDeletionFolderDialog,
    hotkeyDeletionDialog,
    isOpenDialog,
    isOpenContextMenu,
  }

  return (
    <BaseAssetsListContext.Provider value={context}>
      <BaseAssetsList {...rest} items={items} loading={loadingShow} />
    </BaseAssetsListContext.Provider>
  )
}

AssetsListContainer.propTypes = {
  type: PT.SourceFileType.isRequired,
}

// ---

export default AssetsListContainer
